/**
 * As AÇÕES do seletor de cenários (EL-003, lote de 09/10/2026, item 4): Novo, Renomear,
 * Copiar, Arquivar, Restaurar e Excluir de vez. Usadas pela tela do cenário (o seletor no
 * título) e pelo estado vazio do empreendimento (criar o primeiro, restaurar um arquivado).
 *
 * As regras que a tela confere ANTES do clique (vaga, nome repetido) estão em
 * `cenarios-regras.ts`; a regra de verdade é do banco (0028) — se a lista em mãos estiver
 * velha, o banco recusa e a frase traduzida aparece igual.
 */
import { avisar, confirmar, perguntar } from '../ui/aviso';
import { tentar } from '../casca';
import { ir, recarregar } from '../rotas';
import type { AcoesSeletor } from '../ui/seletor-cenario';
import { motivoParaNaoRestaurar, motivoSemVaga, nomeDaCopia, nomeEmUso, proximoAposArquivar } from '../cenarios-regras';
import { avisoDaCopiaNaoPublicada, destinoDaCopia, faltantesOuNada } from '../campos-obrigatorios';
import { entradaDaTela } from '../legado/dados-ponte';
import { cenarioMontado } from '../legado/montagem';
import {
  arquivarCenario, clonarCenario, criarCenario, excluirCenarioDefinitivo, listarCenarios, publicarCenario,
  renomearCenario, restaurarCenario,
} from '../../../../packages/dados/src/repositorios';
import type { CenarioArquivado, CenarioResumo } from '../../../../packages/dados/src/repositorios';
import type { EntradaMotor } from '../../../../packages/dados/src/mapeamento';
import { gravarAgora, temRascunhoNaoPublicado } from '../../../../packages/dados/src/rascunho';

export interface ContextoAcoes {
  empresaId: string;
  empreendimentoId: string;
  /**
   * Os ativos que a tela tem em mãos — base das conferências de vaga e de nome. FUNÇÃO, e não
   * lista (revisão de 09/10/2026): o seletor relê os ativos a cada abertura do painel, e as ações
   * têm de enxergar a MESMA lista — a da abertura da tela ainda trazia o cenário que um colega
   * arquivou, e restaurá-lo era recusado com "já existe um cenário ativo chamado…" (ele mesmo).
   */
  ativos: () => readonly CenarioResumo[];
  /** O cenário aberto; `null` no estado vazio do empreendimento. */
  abertoId: string | null;
  /**
   * A versão que a TELA do aberto mostra (`cenarioCorrente.versao` — sobe com a publicação feita
   * daqui e com a troca da sonda; NÃO sobe quando um colega publica e a tela fica como está).
   * `null` sem cenário aberto. APC-2 (revisão de 12/10/2026): o "Copiar" julga o rascunho por ela,
   * a régua do CAL-3 e da sonda — contra a versão do BANCO, o trabalho que está na tela parecia
   * vencido assim que um colega publicava, e a cópia saía do publicado dele, sem aviso.
   */
  versaoDoAberto: () => number | null;
}

/* ----------------------------------------------------------------------------
 * A CÓPIA QUE NÃO PÔDE SER PUBLICADA
 *
 * Copiar o cenário aberto com alteração não publicada leva o que está na TELA (pedido da
 * v32: a cópia "nascerá com os mesmos dados da que selecionei para copiar"). O jeito normal
 * é publicar a cópia com essa entrada. Quando não dá (faltam campos obrigatórios, ou a
 * publicação foi recusada), a entrada vira o RASCUNHO da cópia — e só a tela da cópia pode
 * gravá-lo, porque o rascunho é uma sessão por cenário aberto. A semente fica aqui até a
 * cópia abrir (`tomarSementeDeRascunho`, chamada por `telaCenario`).
 * ---------------------------------------------------------------------------- */
export interface SementeDeRascunho {
  entrada: EntradaMotor;
  /**
   * POR QUE a cópia não foi publicada (faltam campos — quais —, ou a recusa do servidor).
   * Viaja com a semente e é a TELA DA CÓPIA que o mostra, depois de montar (revisão de
   * 09/10/2026): dito antes de navegar, o aviso durava ~0,4 s — o "Recuperamos o que você tinha
   * digitado" da tela nova o apagava (o aviso é um só), e era falso para uma cópia recém-nascida.
   */
  aviso: string;
}
const sementes = new Map<string, SementeDeRascunho>();

/** Olha sem consumir: a tela lê ANTES de ser adotada (uma vista descartada não pode gastá-la). */
export function verSementeDeRascunho(cenarioId: string): SementeDeRascunho | null {
  return sementes.get(cenarioId) ?? null;
}

/** Consome — só a tela ADOTADA (`aoMostrar`), que é quem grava o rascunho da cópia. */
export function tomarSementeDeRascunho(cenarioId: string): SementeDeRascunho | null {
  const s = sementes.get(cenarioId) ?? null;
  sementes.delete(cenarioId);
  return s;
}

/**
 * Os campos obrigatórios que faltam, pela MESMA função que o "Salvar" do simulador usa.
 * `null` = não deu para conferir (função ausente, erro, resposta estranha) — ORG-2: falha
 * FECHADA, a cópia não é publicada sem conferir (ver `campos-obrigatorios.ts`).
 */
function faltantesDaTela(e: EntradaMotor): string[] | null {
  return faltantesOuNada((window as unknown as { faltantes?: unknown }).faltantes, e);
}

/** Pergunta um nome até ele não repetir um ativo — ou a pessoa desistir. */
async function pedirNome(o: {
  titulo: string; rotulo: string; valor?: string; dica?: string; confirmar: string;
  ativos: readonly CenarioResumo[]; excetoId?: string;
}): Promise<string | null> {
  let valor = o.valor;
  for (;;) {
    const nome = await perguntar({ titulo: o.titulo, rotulo: o.rotulo, valor, dica: o.dica, confirmar: o.confirmar });
    if (nome === null) return null;
    if (!nomeEmUso(o.ativos, nome, o.excetoId ?? null)) return nome;
    avisar(`Já existe um cenário ativo chamado "${nome}" neste empreendimento. Escolha outro nome.`, 'erro');
    valor = nome;
  }
}

/**
 * Abre um cenário. `substituir` TROCA a entrada do histórico em vez de empilhar — para o destino
 * que CORRIGE a rota, em que o endereço anterior deixou de valer ou resolve para o próprio
 * destino (revisão de 09/10/2026): depois de arquivar o aberto, o "voltar" caía no arquivado
 * (para o analista, uma tela de erro: ele não enxerga arquivado); depois de criar o primeiro
 * cenário no estado vazio, o "voltar" reabria o empreendimento, que resolvia de novo para o
 * mesmo cenário — o pingue-pongue que o resolvedor diz evitar. Trocar, copiar e criar com um
 * cenário aberto continuam empilhando: voltar ao cenário anterior é legítimo.
 */
function abrir(cenarioId: string, substituir = false): void {
  ir({ nome: 'cenario', cenarioId }, { substituir });
}

export function acoesDeCenario(ctx: ContextoAcoes): AcoesSeletor {
  const semVaga = (): boolean => {
    const motivo = motivoSemVaga(ctx.ativos().length);
    if (motivo !== null) avisar(motivo, 'aviso');
    return motivo !== null;
  };

  return {
    /* Imediato: o que não foi publicado fica guardado como rascunho daquele cenário (como em
       qualquer outra saída da tela) e volta ao reabrir. */
    trocar: (c) => abrir(c.id),

    novo: () => {
      if (semVaga()) return;
      void (async () => {
        const nome = await pedirNome({
          titulo: 'Novo cenário', rotulo: 'Nome do cenário', dica: 'Ex.: Base, Otimista, Banco',
          confirmar: 'Criar', ativos: ctx.ativos(),
        });
        if (nome === null) return;
        /* no estado vazio, o endereço do empreendimento passa a resolver para este cenário */
        await tentar(() => criarCenario({ empreendimentoId: ctx.empreendimentoId, nome }), (novo) => abrir(novo.id, ctx.abertoId === null));
      })();
    },

    renomear: (c) => {
      void (async () => {
        const nome = await pedirNome({
          titulo: 'Renomear cenário', rotulo: 'Nome', valor: c.nome, confirmar: 'Salvar',
          ativos: ctx.ativos(), excetoId: c.id,
        });
        if (nome === null || nome === c.nome) return;
        /* `recarregar()` remonta a tela: o nome aparece no título, na trilha, no seletor E
           dentro do simulador (Comparação, arquivos exportados), que guarda o nome em
           memória desde a montagem. A publicação não leva mais o nome (CAMPOS_CENARIO). */
        await tentar(() => renomearCenario(c.id, nome), () => {
          avisar('Nome atualizado.', 'ok');
          recarregar();
        });
      })();
    },

    copiar: (c) => {
      if (semVaga()) return;
      void (async () => {
        const nome = await pedirNome({
          titulo: 'Copiar cenário', rotulo: 'Nome da cópia', valor: nomeDaCopia(ctx.ativos(), c.nome),
          confirmar: 'Copiar', ativos: ctx.ativos(),
        });
        if (nome === null) return;

        /* O que a cópia leva: o cenário ABERTO com alteração não publicada leva a TELA;
           qualquer outro leva o publicado (é o que `clonar_cenario` copia). "Não publicada" pela
           versão DA TELA (APC-2): o rascunho feito sobre ela é o que está na tela, mesmo que um
           colega já tenha publicado uma mais nova. */
        let daTela: EntradaMotor | null = null;
        if (ctx.abertoId === c.id && cenarioMontado() === c.id) {
          await gravarAgora();
          const versaoDaTela = ctx.versaoDoAberto() ?? undefined;
          if (await temRascunhoNaoPublicado(c.id, versaoDaTela).catch(() => false)) daTela = entradaDaTela();
        }

        let novoId: string;
        try {
          novoId = await clonarCenario(c.id, nome);
        } catch (e) {
          const codigo = (e as { codigo?: string } | null)?.codigo;
          avisar(
            codigo === 'EXTENSAO_SO_GESTOR'
              ? 'Este cenário tem extensão de prazo da demanda autorizada por um gestor, e só um gestor pode copiá-lo: a autorização não passa sozinha para a cópia. Peça a um gestor para fazer a cópia.'
              : e instanceof Error ? e.message : String(e),
            'erro',
          );
          return;
        }

        if (daTela === null) {
          avisar(`Cópia "${nome}" criada.`, 'ok');
          abrir(novoId);
          return;
        }

        const faltam = faltantesDaTela(daTela);
        if (destinoDaCopia(faltam) === 'semente') {
          sementes.set(novoId, { entrada: daTela, aviso: avisoDaCopiaNaoPublicada(nome, faltam) });
          abrir(novoId);
          return;
        }
        try {
          const copia = (await listarCenarios(ctx.empreendimentoId)).find((x) => x.id === novoId);
          if (copia === undefined) throw new Error('a cópia criada não foi encontrada para publicar');
          /* A cópia leva o cadastro DA TELA (0032): o cadastro é de cada cenário, e publicar a
             cópia grava o cadastro só nela — o original, os irmãos e o empreendimento não mudam.
             (Até a 0032 o cadastro era do empreendimento e a cópia o deixava de fora: a edição
             não publicada do cadastro se perdia na cópia.) */
          await publicarCenario({
            cenario: copia, entrada: daTela,
            rotulo: 'Cópia', motivo: `Cópia de "${c.nome}" com o que estava na tela`,
          });
          avisar(`Cópia "${nome}" criada e publicada com o que estava na tela.`, 'ok');
        } catch (e) {
          sementes.set(novoId, {
            entrada: daTela,
            aviso: `Cópia "${nome}" criada, mas a publicação dela foi recusada (${e instanceof Error ? e.message : String(e)}). O que estava na tela ficou como alteração não publicada da cópia.`,
          });
        }
        abrir(novoId);
      })();
    },

    arquivar: (c) => {
      void (async () => {
        const eraAberto = ctx.abertoId === c.id;
        /* O que estava pendente sobe ANTES: o rascunho fica guardado com o cenário e volta
           se ele for restaurado. */
        if (eraAberto) await gravarAgora();
        await tentar(() => arquivarCenario(c.id), () => {
          const desfazer = (): void => {
            void tentar(() => restaurarCenario(c.id), () => {
              avisar(`"${c.nome}" restaurado.`, 'ok');
              /* substituindo: o histórico volta a ser o de antes de arquivar */
              if (eraAberto) abrir(c.id, true);
              else recarregar();
            });
          };
          avisar(`"${c.nome}" arquivado — não ocupa mais uma das 5 vagas e pode ser restaurado em "Arquivados".`, {
            tom: 'ok',
            desfazer,
          });
          if (!eraAberto) {
            recarregar();
            return;
          }
          /* SUBSTITUINDO: o endereço do arquivado deixou de valer — no histórico, o "voltar" o
             reabriria (e o analista, que não enxerga arquivado, cairia numa tela de erro). */
          const proximo = proximoAposArquivar(ctx.ativos(), c.id);
          if (proximo !== null) abrir(proximo, true);
          else ir({ nome: 'empreendimento', empresaId: ctx.empresaId, empreendimentoId: ctx.empreendimentoId }, { substituir: true });
        });
      })();
    },

    restaurar: (a: CenarioArquivado) => {
      /* o próprio arquivado sai da conta — ver `motivoParaNaoRestaurar` */
      const motivo = motivoParaNaoRestaurar(ctx.ativos(), a);
      if (motivo !== null) {
        avisar(motivo, 'erro');
        return;
      }
      void tentar(() => restaurarCenario(a.id), () => {
        avisar(`"${a.nome}" restaurado.`, 'ok');
        /* No estado vazio não há o que recarregar: o restaurado é o único ativo — abre, no
           lugar do endereço do empreendimento (que agora resolveria para ele). */
        if (ctx.abertoId === null) abrir(a.id, true);
        else recarregar();
      });
    },

    excluir: (a: CenarioArquivado) => {
      void (async () => {
        const versoes = a.versaoPublicada === 0
          ? 'Ele nunca foi publicado.'
          : a.versaoPublicada === 1
            ? 'A versão publicada dele também será apagada.'
            : `As ${a.versaoPublicada} versões publicadas dele também serão apagadas.`;
        const primeiro = await confirmar({
          titulo: 'Excluir definitivamente?',
          corpo: `O cenário "${a.nome}" será apagado de vez, junto com os rascunhos de toda a equipe. ${versoes} Esta ação não pode ser desfeita.`,
          confirmar: 'Continuar',
          perigo: true,
        });
        if (!primeiro) return;
        const segundo = await confirmar({
          titulo: 'Confirmar a exclusão definitiva',
          corpo: `Depois de excluído, "${a.nome}" não volta — nem pelo administrador. Arquivado, ele já não ocupa vaga. Excluir mesmo assim?`,
          confirmar: 'Sim, excluir definitivamente',
          perigo: true,
        });
        if (!segundo) return;
        await tentar(() => excluirCenarioDefinitivo(a.id), () => {
          avisar(`"${a.nome}" foi excluído definitivamente.`, 'ok');
          /* O excluído era o ABERTO (o Admin Hype que é gestor abre um arquivado por link antigo,
             ou um colega arquivou o que ele tinha aberto): recarregar refaria a rota de um cenário
             que não existe mais — "Erro / Registro não encontrado" logo depois do sucesso. O
             empreendimento resolve para outro ativo ou para o estado vazio. Substituindo: o
             endereço do excluído não pode ficar no "voltar". */
          if (ctx.abertoId === a.id) {
            ir({ nome: 'empreendimento', empresaId: ctx.empresaId, empreendimentoId: ctx.empreendimentoId }, { substituir: true });
          } else {
            recarregar();
          }
        });
      })();
    },
  };
}
